import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Grid,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { FormikProps } from 'formik';
import { InvoiceDetailsSettingsFormData } from 'models/forms';
import PageTitle from 'components/shared/page-title/page-title.component';
import {
    hasDanishPaymentFields,
    hasFinnishPaymentFields,
    hasNorwegianPaymentFields,
    hasSwedishGiroFields,
    KidCheckDigitMethod,
    PaymentBoxAccount,
} from 'utils/payment-details-validation';
import DanishPaymentFields from './danish-payment-fields.component';
import FinnishPaymentFields from './finnish-payment-fields.component';

type PaymentField = 'bankgiro' | 'postgiro' | 'iban' | 'bicSwift' | 'accountNumber' | 'kidLength';

type PaymentDetailsFieldsProps = {
    formik: FormikProps<InvoiceDetailsSettingsFormData>;
    country?: string;
};

const countryDisplayName = (country: string, language: string) => {
    try {
        return new Intl.DisplayNames([language.replace('_', '-')], { type: 'region' }).of(country) || country;
    } catch {
        return country;
    }
};

/** DD-3700: a Norwegian lab's account number and KID agreement. */
const NorwegianPaymentFields: FC<{ formik: FormikProps<InvoiceDetailsSettingsFormData> }> = ({ formik }) => {
    const { t: translate } = useTranslation(['lab-settings']);
    const fieldError = (field: PaymentField) => {
        const message = formik.touched[field] ? formik.errors[field] : undefined;
        return { helperText: message, error: Boolean(message) };
    };
    const accountNumberError = fieldError('accountNumber');
    const methodError = formik.errors.kidCheckDigitMethod;

    return (
        <>
            <Grid item sm={12} md={6}>
                <TextField
                    id="accountNumber"
                    label={translate('lab-settings:ACCOUNT_NUMBER')}
                    fullWidth
                    {...formik.getFieldProps('accountNumber')}
                    {...accountNumberError}
                    helperText={accountNumberError.helperText || translate('lab-settings:ACCOUNT_NUMBER_INFO')}
                />
            </Grid>
            <Grid item xs={12}>
                <FormControlLabel
                    control={<Checkbox {...formik.getFieldProps({ name: 'kidEnabled', type: 'checkbox' })} />}
                    label={translate('lab-settings:KID_ENABLED')}
                />
                <Typography variant="body2" color="text.secondary">
                    {translate('lab-settings:KID_INFO')}
                </Typography>
            </Grid>
            {formik.values.kidEnabled ? (
                <>
                    <Grid item sm={12} md={6}>
                        <TextField
                            id="kidLength"
                            type="number"
                            label={translate('lab-settings:KID_LENGTH')}
                            fullWidth
                            inputProps={{ min: 2, max: 25 }}
                            {...formik.getFieldProps('kidLength')}
                            {...fieldError('kidLength')}
                        />
                    </Grid>
                    <Grid item sm={12} md={6}>
                        <FormControl fullWidth error={Boolean(methodError)}>
                            <InputLabel id="kid-check-digit-method-label">
                                {translate('lab-settings:KID_CHECK_DIGIT_METHOD')}
                            </InputLabel>
                            <Select
                                labelId="kid-check-digit-method-label"
                                label={translate('lab-settings:KID_CHECK_DIGIT_METHOD')}
                                {...formik.getFieldProps('kidCheckDigitMethod')}
                            >
                                <MenuItem value={KidCheckDigitMethod.MOD10}>MOD10</MenuItem>
                                <MenuItem value={KidCheckDigitMethod.MOD11}>MOD11</MenuItem>
                            </Select>
                            {methodError ? <FormHelperText>{methodError}</FormHelperText> : undefined}
                        </FormControl>
                    </Grid>
                </>
            ) : undefined}
        </>
    );
};

/**
 * DD-3700: the "Receive payments" part of the invoice settings. Which fields show depends on
 * the lab's country: a Swedish lab can be paid by bankgiro and plusgiro, a Norwegian lab to
 * its account number with KID, a Danish lab to reg.nr. + account number with FI, every lab by IBAN.
 */
const PaymentDetailsFields: FC<PaymentDetailsFieldsProps> = ({ formik, country }: PaymentDetailsFieldsProps) => {
    const { t: translate, i18n } = useTranslation(['lab-settings']);
    const labCountry = (country || 'SE').toUpperCase();
    const showGiroFields = hasSwedishGiroFields(labCountry);
    const showNorwegianFields = hasNorwegianPaymentFields(labCountry);
    const showDanishFields = hasDanishPaymentFields(labCountry);
    const showFinnishFields = hasFinnishPaymentFields(labCountry);

    const fieldError = (field: PaymentField) => {
        const message = formik.touched[field] ? formik.errors[field] : undefined;
        return { helperText: message, error: Boolean(message) };
    };

    return (
        <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
                <PageTitle headingStyle="h3" title={translate('lab-settings:RECEIVE_PAYMENTS')} />
                <Typography variant="body2" color="text.secondary">
                    {translate('lab-settings:RECEIVE_PAYMENTS_INFO', {
                        country: countryDisplayName(labCountry, i18n.language),
                    })}
                </Typography>
            </Grid>

            {showGiroFields ? (
                <>
                    <Grid item sm={12} md={6}>
                        <TextField
                            id="bankgiro"
                            label={translate('lab-settings:BANKGIRO')}
                            fullWidth
                            {...formik.getFieldProps('bankgiro')}
                            {...fieldError('bankgiro')}
                        />
                    </Grid>
                    <Grid item sm={12} md={6}>
                        <TextField
                            id="postgiro"
                            label={translate('lab-settings:POSTGIRO')}
                            fullWidth
                            {...formik.getFieldProps('postgiro')}
                            {...fieldError('postgiro')}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl>
                            <FormLabel id="payment-box-account-label">
                                {translate('lab-settings:PAYMENT_BOX_ACCOUNT')}
                            </FormLabel>
                            <RadioGroup
                                row
                                aria-labelledby="payment-box-account-label"
                                {...formik.getFieldProps('paymentBoxAccount')}
                            >
                                <FormControlLabel
                                    value={PaymentBoxAccount.BANKGIRO}
                                    control={<Radio />}
                                    label={translate('lab-settings:BANKGIRO')}
                                    disabled={!formik.values.bankgiro}
                                />
                                <FormControlLabel
                                    value={PaymentBoxAccount.PLUSGIRO}
                                    control={<Radio />}
                                    label={translate('lab-settings:POSTGIRO')}
                                    disabled={!formik.values.postgiro}
                                />
                            </RadioGroup>
                            {formik.errors.paymentBoxAccount ? (
                                <Typography variant="caption" color="error">
                                    {formik.errors.paymentBoxAccount}
                                </Typography>
                            ) : undefined}
                        </FormControl>
                    </Grid>
                </>
            ) : undefined}

            {showNorwegianFields ? <NorwegianPaymentFields formik={formik} /> : undefined}
            {showDanishFields ? <DanishPaymentFields formik={formik} /> : undefined}

            <Grid item sm={12} md={6}>
                <TextField
                    id="iban"
                    label={translate('lab-settings:IBAN_ACCOUNT_NUMBER')}
                    fullWidth
                    {...formik.getFieldProps('iban')}
                    {...fieldError('iban')}
                    helperText={
                        (formik.touched.iban && formik.errors.iban) ||
                        (showGiroFields || showNorwegianFields || showDanishFields
                            ? translate('lab-settings:IBAN_FOREIGN_CUSTOMERS')
                            : undefined)
                    }
                />
            </Grid>
            <Grid item sm={12} md={6}>
                <TextField
                    id="bicSwift"
                    label={translate('lab-settings:BIC_SWIFT')}
                    fullWidth
                    {...formik.getFieldProps('bicSwift')}
                    {...fieldError('bicSwift')}
                />
            </Grid>
            {showFinnishFields ? <FinnishPaymentFields formik={formik} /> : undefined}
        </Grid>
    );
};

export default PaymentDetailsFields;
